/**
 * FieldComboSheet (Bob walkthrough 09-22, Wave 2 item 5) — the field zone's
 * searchable location picker with create-new. BottomSheetSelect's sheet, plus
 * a text input pinned at the top and a `Use "…"` row.
 *
 * Why it exists: the seven location fields were plain text inputs, so each
 * rep typed their own spelling ("Bay 7", "BAY7", "bay 7 ") and location values
 * drifted. This sheet offers what the site already uses first, and makes a
 * new value a deliberate, visible act.
 *
 * PRESENTATION + OPTION DERIVATION ONLY. Like BottomSheetSelect, the PARENT
 * owns commit-if-changed and the close navigation: a row tap hands back the
 * value (null for "— none —") through onSelect, and every other close path
 * goes through onClose. It does not import react-router.
 *
 * ⛔ COMMIT HAPPENS ON TAP ONLY. There is no onBlur and no commit on close:
 * backdrop, Escape, drag-down and hardware/gesture back all reach onClose,
 * never onSelect, so dismissing leaves the field exactly as it was. Enter on
 * the keyboard does nothing either — a typed value becomes a commit only
 * when the rep taps its `Use "…"` row.
 *
 * Options, in this order, merged and de-duplicated case-insensitively (the
 * FIRST casing seen wins), then sorted with TEXT_COLLATOR; SITE LOC and
 * SECTION / DETAIL are then re-ordered most-used-first by rankByUsage
 * (stable, so ties and never-used values keep the collated order):
 *   1. getFieldUniqueValues(records, fieldKey) — values on this site's loaded
 *      rows (filterRecords.js), unchanged.
 *   2. getSessionValues(fieldKey) — values created this session and not yet
 *      back from a Load (sessionVocabulary.js).
 *   3. (slot — see buildComboOptions) site-scoped ReferenceData.
 *
 * FORCE-UPPER INPUT (amendment to d294686). The search box uppercases as the
 * rep types, caret kept where it was — the reference build's forceUpper
 * (toolsight-inv-v11_11.html:9958-9965, add-row value :9940). Real location
 * data is uppercase (PLANT 2, YARD) and normalize-on-write uppercases enums
 * platform-wide, so a typed "bay 7" must not become a second "BAY 7".
 * Only the TYPED text is uppercased: options render and commit their stored
 * value verbatim, and nothing already on a record is touched.
 *
 * Create row: shown at the TOP of the list when the trimmed text matches no
 * option case-insensitively. It reads and commits the UPPERCASED, trimmed
 * text — trimmed as TextEditor trims, so "BAY 7 " cannot become a second
 * location either. The de-dupe and the filter stay case-insensitive, so a
 * stored "Bay 7" still suppresses the create row for a typed "bay 7".
 *
 * "— none —" row: mirrors BottomSheetSelect (:128) so a location can still
 * be cleared, which the text input allowed. Shown only while the search box
 * is empty; once the rep is typing it is noise.
 *
 * Stacking: same as BottomSheetSelect — inside the ModalShell dialog subtree,
 * no portal, z-index --z-modal, later in the stacking context.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getFieldUniqueValues } from '../data/filterRecords';
import { TEXT_COLLATOR } from '../data/fieldValueRules';
import { getSessionValues, addSessionValue } from '../data/sessionVocabulary';
import { rankByUsage } from '../data/rankByUsage';
import './FieldComboSheet.css';

const DRAG_CLOSE_PX = 80; // BottomSheetSelect's threshold

// The location fields this sheet ranks (P2-3). The five _nbr fields share the
// sheet but are NOT ranked — SKID NBR was ruled out of the ranking work.
const RANKED_KEYS = new Set(['site_location', 'detail_location']);

const fold = (s) => String(s).toLowerCase();

/**
 * The merged option list for one field. Exported so the merge rule has one
 * definition that tests can hold directly.
 */
export function buildComboOptions(records, fieldKey) {
  const sources = [
    getFieldUniqueValues(records, fieldKey),
    getSessionValues(fieldKey),
    // 3. SLOT, NOT BUILT: site-scoped ReferenceData — the vocabulary the
    //    desktop's ComboBoxes read through useReferenceData (site_location /
    //    detail_location, ToolDetailScreen.jsx:261-263). The field zone does
    //    not fetch it today (FIELD_REF_KEYS, fieldRefData.js:31). When added,
    //    append it HERE, after the rows and the session, so a value the rows
    //    already carry keeps the rows' casing.
  ];
  const byFold = new Map();
  for (const list of sources) {
    for (const v of list) {
      const s = String(v);
      const k = fold(s);
      if (!byFold.has(k)) byFold.set(k, s);
    }
  }
  return [...byFold.values()].sort(TEXT_COLLATOR.compare);
}

export default function FieldComboSheet({
  isOpen,
  label,
  fieldKey,
  value,
  records,
  onSelect,
  onClose,
}) {
  const [query, setQuery] = useState('');
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(null);

  // Escape (desktop) — capture + stopPropagation: close the SHEET only, never
  // ModalShell. Same as BottomSheetSelect.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, onClose]);

  // Every open starts clean: no leftover search text, no half-finished drag.
  useEffect(() => {
    setQuery('');
    setDragY(0);
    dragStartY.current = null;
  }, [isOpen, fieldKey]);

  // Built and ranked once per open, never per keystroke: the search term is
  // deliberately NOT a dependency, so typing filters this ranked list and never
  // re-ranks it. isOpen IS one, so every open rebuilds and picks up a value
  // created this session (module state React cannot see) on the sheet's own
  // terms, whatever the host passes for fieldKey while the sheet is closed.
  const options = useMemo(() => {
    if (!isOpen || !fieldKey) return [];
    const merged = buildComboOptions(records, fieldKey);
    return RANKED_KEYS.has(fieldKey) ? rankByUsage(merged, records, fieldKey) : merged;
  }, [records, fieldKey, isOpen]);

  if (!isOpen) return null;

  const typed = query.trim();
  const needle = fold(typed);
  const shown = typed === '' ? options : options.filter((o) => fold(o).includes(needle));
  const hasExact = typed !== '' && options.some((o) => fold(o) === needle);
  const showCreate = typed !== '' && !hasExact;
  const current = value === null || value === undefined ? '' : String(value);
  const showCurrentExtra = typed === '' && current !== '' && !options.includes(current);

  const handleDragStart = (e) => {
    dragStartY.current = e.touches[0].clientY;
  };
  const handleDragMove = (e) => {
    if (dragStartY.current == null) return;
    setDragY(Math.max(0, e.touches[0].clientY - dragStartY.current));
  };
  const handleDragEnd = () => {
    if (dragStartY.current == null) return;
    const shouldClose = dragY > DRAG_CLOSE_PX;
    dragStartY.current = null;
    setDragY(0);
    if (shouldClose) onClose();
  };

  // forceUpper, the reference build's way (v11_11 :9958-9965): uppercase the
  // DOM value in place, then put the caret back where the rep left it. The
  // DOM is written BEFORE setQuery, so when React re-renders the input its
  // value already matches and React does not write it again — a write there
  // is what would throw the caret to the end.
  const handleQueryInput = (e) => {
    const el = e.target;
    const pos = el.selectionStart;
    el.value = el.value.toUpperCase();
    try { el.setSelectionRange(pos, pos); } catch { /* no selection API on this input */ }
    setQuery(el.value);
  };

  // `typed` is already uppercase: handleQueryInput is the only writer of
  // `query` apart from the per-open reset to ''.
  const handleCreate = () => {
    addSessionValue(fieldKey, typed);
    onSelect(typed);
  };

  const renderRow = (optValue, display, extraClass = '') => {
    const selected = current === (optValue ?? '');
    return (
      <button
        key={optValue === null ? '__none__' : optValue}
        type="button"
        className={`fcs-option${extraClass}${selected ? ' fcs-option-selected' : ''}`}
        role="option"
        aria-selected={selected}
        data-value={optValue ?? ''}
        onClick={() => onSelect(optValue)}
      >
        <span className="fcs-option-label">{display}</span>
        {selected && <span className="fcs-option-check" aria-hidden="true">✓</span>}
      </button>
    );
  };

  return (
    <div data-testid="field-combo-sheet">
      <div className="fcs-backdrop" data-testid="fcs-backdrop" onClick={onClose} />
      <div
        className="fcs-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={label}
        style={dragY > 0 ? { transform: `translateY(${dragY}px)`, animation: 'none' } : undefined}
      >
        <div
          className="fcs-grab"
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onTouchCancel={handleDragEnd}
        >
          <div className="fcs-handle" aria-hidden="true" />
          <div className="fcs-title">{label}</div>
        </div>
        <div className="fcs-search">
          <input
            type="text"
            className="fcs-input"
            data-testid="fcs-input"
            value={query}
            onChange={handleQueryInput}
            placeholder="Search or type a new value"
            aria-label={`Search ${label}`}
            autoFocus
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
        </div>
        <div className="fcs-options" role="listbox" aria-label={`${label} options`}>
          {showCreate && (
            <button
              key="__create__"
              type="button"
              className="fcs-option fcs-option-create"
              role="option"
              aria-selected={false}
              data-testid="fcs-create"
              onClick={handleCreate}
            >
              <span className="fcs-option-label">Use &quot;{typed}&quot;</span>
            </button>
          )}
          {typed === '' && renderRow(null, '— none —', ' fcs-option-none')}
          {shown.map((opt) => renderRow(opt, opt))}
          {showCurrentExtra && renderRow(current, current)}
        </div>
      </div>
    </div>
  );
}
